import { useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import _, { isNil } from "lodash"
import { ImdfFeature } from "providers/venue/types"

interface IFloorOption {
  id: string | number
  levelName?: string
  ordinal?: number
}

interface IUseOccupantFloorsResult {
  floorOptions: IFloorOption[]
  currentFloor?: string
  currentFloorOrdinal?: number
  hasFloors: boolean
}

type UseOccupantFloors = (feature?: ImdfFeature) => IUseOccupantFloorsResult

export const useOccupantFloors: UseOccupantFloors = (feature) => {
  const [searchParams] = useSearchParams()

  const relatedSpaces = useMemo(() => {
    const { units = [], kiosks = [] } = feature?.properties || {}
    return [...units, ...kiosks]
  }, [feature])

  const floorOptions = useMemo((): IFloorOption[] => {
    if (feature?.feature_type !== "occupant") return []

    const occupantId = feature.id
    const mainOrdinal = feature.properties?.ordinal

    // Main floor (current occupant level)
    const mainFloorOption: IFloorOption = {
      id: occupantId,
      levelName: feature.properties?.level_name,
      ordinal: mainOrdinal,
    }

    // Other floors from related location
    const transformedOtherFloorOptions = relatedSpaces.map(
      (item): IFloorOption => ({
        id: `${occupantId}?target=${item.id}`,
        levelName: item.properties?.level?.properties?.name?.en,
        ordinal: item.properties?.level?.properties?.ordinal,
      })
    )
    const filteredOtherFloorOptions = transformedOtherFloorOptions.filter(
      (item) => !isNil(item.ordinal) && item.ordinal !== mainOrdinal
    )

    const floorOptions = [mainFloorOption, ...filteredOtherFloorOptions]
    const sortedFloorOptionWithOrdinal = _.orderBy(
      floorOptions,
      ["ordinal"],
      ["desc"]
    )
    return sortedFloorOptionWithOrdinal
  }, [feature, relatedSpaces])

  const currentFloor = useMemo((): string | undefined => {
    const targetId = searchParams.get("target")
    const occupantId = feature?.id

    if (!occupantId) return

    return targetId ? `${occupantId}?target=${targetId}` : `${occupantId}`
  }, [searchParams, feature?.id])

  const currentFloorOrdinal = useMemo((): number | undefined => {
    const targetParam = searchParams.get("target")
    const mainOrdinal = feature?.properties?.ordinal

    if (targetParam && feature?.properties) {
      const matchedSpace = relatedSpaces.find((item) => item.id === targetParam)
      return matchedSpace?.properties?.level?.properties?.ordinal
    }

    return mainOrdinal
  }, [searchParams, relatedSpaces, feature])

  return {
    floorOptions,
    currentFloor,
    currentFloorOrdinal,
    hasFloors: floorOptions.length > 1,
  }
}
